/**
 * ExpandableSections Component - Progressive disclosure container
 *
 * Phase 3: Expandable Sections - Advanced features without clutter
 *
 * Features:
 * - Sources panel for detailed citation information
 * - Debug panel with raw JSON and metrics
 * - Smooth expand/collapse animations
 * - Collapsed by default for clean initial view
 * - Keyboard navigation support
 *
 * Design Principles:
 * - Progressive disclosure - advanced features available but not intrusive
 * - Accessibility - proper ARIA labels and keyboard navigation
 * - Smooth animations - enhance UX without being distracting
 * - Consistent styling with existing Allianz brand colors
 */

import React, { useState } from 'react';
import { Message } from '../types/chat';
import { DebugPanel } from './DebugPanel';

interface ExpandableSectionsProps {
  message: Message;
  showDebug?: boolean;
}

interface SectionState {
  sources: boolean;
  debug: boolean;
}

export const ExpandableSections: React.FC<ExpandableSectionsProps> = ({
  message,
  showDebug = true,
}) => {
  // Clean debug logging - only log meaningful information
  if (
    message.sender === 'agent' &&
    (message.traces?.length || message.collaboration_log?.length)
  ) {
    console.log(
      '📊 ExpandableSections: Processing agent response with traces/collaboration data',
    );
  }

  const [expanded, setExpanded] = useState<SectionState>({
    sources: false, // Collapse sources by default
    debug: false,
  });

  const toggleSection = (section: keyof SectionState) => {
    setExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const hasDebugInfo =
    showDebug &&
    (message.debug ||
      message.traces ||
      message.processing_steps ||
      message.debug_info ||
      message.performance_metrics);

  // Don't render if no expandable content
  if (!hasDebugInfo) {
    return null;
  }

  return (
    <div className="expandable-sections border-t border-gray-100 mt-2">
      {/* Debug Section */}
      {hasDebugInfo && process.env.NODE_ENV === 'development' && (
        <div className="debug-section border-t border-gray-100 mt-1">
          <button
            onClick={() => toggleSection('debug')}
            className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors duration-200"
            aria-expanded={expanded.debug}
            aria-controls="debug-content"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  🔧 Debug Information
                </span>
                <span className="text-xs text-gray-500">
                  ({message.traces?.length || 0} traces,{' '}
                  {message.processing_steps?.length || 0} steps,{' '}
                  {message.debug_info ? 'enhanced' : 'basic'})
                </span>
              </div>
              <div
                className="text-gray-400 transition-transform duration-200"
                style={{
                  transform: expanded.debug ? 'rotate(90deg)' : 'rotate(0deg)',
                }}
              >
                ▶
              </div>
            </div>
          </button>

          {/* Debug Content */}
          {process.env.NODE_ENV === 'development' && (
            <div
              id="debug-content"
              className={`transition-all duration-300 ease-in-out ${
                expanded.debug ? 'block opacity-100' : 'hidden opacity-0'
              }`}
              aria-hidden={!expanded.debug}
              style={{ maxHeight: expanded.debug ? 'none' : '0' }}
            >
              <div className="px-4 pb-4">
                <DebugPanel
                  debug={message.debug}
                  traces={message.traces || []}
                  collaborationLog={message.collaboration_log || []}
                  completeMessage={message}
                  processing_steps={message.processing_steps || []}
                  debug_info={message.debug_info}
                  performance_metrics={message.performance_metrics}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
